import type { BrowserOptions } from '@sentry/react';

/**
 * Options passed to Sentry.replayIntegration.
 *
 * block: ['iframe'] is important on the platform specifically: Session Replay's
 * underlying rrweb library throws cross-origin SecurityError when it tries to
 * attach observers into iframes whose contentDocument it cannot read — and the
 * platform embeds Auth0 silent-auth and Stripe Checkout/Elements iframes on the
 * billing pages. Ignoring iframe contents avoids that noise. Mirrors the chat
 * frontend's sentryConfig (Sentry issue CHAT-MARITACA-Y).
 */
export const replayIntegrationOptions = {
    maskAllInputs: true,
    maskAllText: false,
    blockAllMedia: false,
    unblock: ['.MuiModal-root', '.MuiDialog-root', '[role="dialog"]'],
    mask: ['input[type="password"]', 'input[type="email"]', '[data-replay-mask]'],
    block: ['iframe'],
};

/**
 * Error patterns to drop before they hit Sentry. These come from inside the
 * Sentry SDK or rrweb itself (not user-facing bugs), so capturing them only
 * inflates noise and quota.
 */
export const sentryIgnoreErrors: (string | RegExp)[] = [
    /Failed to read a named property 'Element' from 'Window'/,
];

/**
 * Normalise the build-time release tag injected through
 * REACT_APP_SENTRY_RELEASE so it can be passed to Sentry.init.
 *
 * The Dockerfile sets REACT_APP_SENTRY_RELEASE at build time and uploads
 * sourcemaps to Sentry tagged with the same value. The SDK must report events
 * with that exact release for sourcemap association to work — without it, every
 * event lands with release=null and stacks stay unsymbolicated.
 *
 * Returns undefined (never the literal string "undefined") when no real tag is
 * available, so Sentry skips the release field instead of creating a release
 * literally named "undefined".
 */
export const buildSentryReleaseTag = (raw: string | undefined): string | undefined => {
    const trimmed = (raw ?? '').trim();
    if (!trimmed || trimmed === 'undefined') return undefined;
    return trimmed;
};

/**
 * Top-level Sentry.init options whose shape is testable independently of the
 * DSN / integrations array (which can only be constructed at runtime).
 */
export const sentryInitOptions: Pick<
    BrowserOptions,
    | 'sendDefaultPii'
    | 'tracesSampleRate'
    | 'replaysSessionSampleRate'
    | 'replaysOnErrorSampleRate'
    | 'enableLogs'
    | 'ignoreErrors'
    | 'release'
    | 'attachStacktrace'
> = {
    sendDefaultPii: true,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
    // Capture a stack even when an error arrives without one (e.g. a
    // window.onerror with no Error object), so handled reports made from a
    // catch block that lost the original stack still point somewhere useful.
    attachStacktrace: true,
    ignoreErrors: sentryIgnoreErrors,
    release: buildSentryReleaseTag(process.env.REACT_APP_SENTRY_RELEASE),
};

/**
 * Tag stamped on every handled (non-crash) error reported via utils/reportError.
 * Used both to filter these events in the Sentry UI and to opt them out of
 * Session Replay below.
 */
export const HANDLED_TAG = 'handled';

/**
 * beforeErrorSampling predicate for Session Replay (wired in index.tsx).
 *
 * replaysOnErrorSampleRate is 1.0, so by default EVERY error-type event that
 * reaches Sentry pulls a full session replay. Our handled-error instrumentation
 * (utils/reportError) can emit many low-severity `warning` events; recording a
 * replay for each would blow the Session Replay quota. Sentry's replay
 * error-mode triggers on any error-TYPE event regardless of level, so level
 * alone does not protect us — we explicitly opt handled events out of replay
 * here. The error event itself is still sent; only the replay is skipped.
 *
 *   returns true  -> Sentry may record/keep the replay for this event
 *   returns false -> skip replay for this event
 */
type ReplaySamplingEvent = { tags?: { [key: string]: unknown } };
export const shouldRecordReplayForEvent = (event: ReplaySamplingEvent): boolean => {
    const handled = event?.tags?.[HANDLED_TAG];
    return handled !== 'true' && handled !== true;
};
